import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { chatAPI } from '../../services/api';

interface MyNotesInterfaceProps {
  onViewSession?: (sessionId: string) => void;
}

const MyNotesInterface: React.FC<MyNotesInterfaceProps> = ({ onViewSession }) => {
  const [myNotesData, setMyNotesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNoteTypeFilter, setSelectedNoteTypeFilter] = useState<string | null>(null);
  const [noteTypeCounts, setNoteTypeCounts] = useState({
    general: 0,
    problem: 0,
    suggestion: 0,
    knowledgebase: 0,
  });
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });

  const loadMyNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await chatAPI.getUserSessionsWithNotes(
        selectedNoteTypeFilter || undefined,
        pagination.page,
        pagination.pageSize
      );
      setMyNotesData(response.results);
      setNoteTypeCounts(response.note_type_counts);
      setPagination(prev => ({ ...prev, count: response.count }));
    } catch (err: any) {
      console.error('Error loading my notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMyNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNoteTypeFilter, pagination.page]);

  const handleNoteTypeFilterChange = (noteType: string | null) => {
    setSelectedNoteTypeFilter(noteType);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const handleViewSession = (sessionId: string) => {
    if (onViewSession) {
      onViewSession(sessionId);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalSessions = noteTypeCounts.general + noteTypeCounts.problem + noteTypeCounts.suggestion + noteTypeCounts.knowledgebase;

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: '#f8fafc',
        overflow: 'hidden',
      }}
    >
      {/* Header Section */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
          color: 'white',
          py: 6,
          px: 4,
          flexShrink: 0,
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700, 
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            📝 My Notes
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400, mb: 3 }}>
            View and manage all your session notes in one place
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 4 }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Filter Section */}
          <Card 
            sx={{ 
              mb: 3, 
              p: 3,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Filter by Note Type
              </Typography>
              <IconButton
                onClick={loadMyNotes}
                disabled={loading}
                sx={{
                  color: 'success.main',
                  '&:hover': {
                    bgcolor: 'rgba(5, 150, 105, 0.08)',
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip
                label={`All (${totalSessions})`}
                onClick={() => handleNoteTypeFilterChange(null)}
                color={selectedNoteTypeFilter === null ? 'success' : 'default'}
                variant={selectedNoteTypeFilter === null ? 'filled' : 'outlined'}
                sx={{ 
                  fontWeight: selectedNoteTypeFilter === null ? 600 : 500,
                  fontSize: '0.875rem',
                  px: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  },
                }}
              />
              <Chip
                label={`General (${noteTypeCounts.general})`}
                onClick={() => handleNoteTypeFilterChange('general')}
                color={selectedNoteTypeFilter === 'general' ? 'primary' : 'default'}
                variant={selectedNoteTypeFilter === 'general' ? 'filled' : 'outlined'}
                sx={{ 
                  fontWeight: selectedNoteTypeFilter === 'general' ? 600 : 500,
                  fontSize: '0.875rem',
                  px: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  },
                }}
              />
              <Chip
                label={`Problem (${noteTypeCounts.problem})`}
                onClick={() => handleNoteTypeFilterChange('problem')}
                color={selectedNoteTypeFilter === 'problem' ? 'error' : 'default'}
                variant={selectedNoteTypeFilter === 'problem' ? 'filled' : 'outlined'}
                sx={{ 
                  fontWeight: selectedNoteTypeFilter === 'problem' ? 600 : 500,
                  fontSize: '0.875rem',
                  px: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  },
                }}
              />
              <Chip
                label={`Suggestion (${noteTypeCounts.suggestion})`}
                onClick={() => handleNoteTypeFilterChange('suggestion')}
                color={selectedNoteTypeFilter === 'suggestion' ? 'success' : 'default'}
                variant={selectedNoteTypeFilter === 'suggestion' ? 'filled' : 'outlined'}
                sx={{ 
                  fontWeight: selectedNoteTypeFilter === 'suggestion' ? 600 : 500,
                  fontSize: '0.875rem',
                  px: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  },
                }}
              />
              <Chip
                label={`Knowledgebase (${noteTypeCounts.knowledgebase})`}
                onClick={() => handleNoteTypeFilterChange('knowledgebase')}
                color={selectedNoteTypeFilter === 'knowledgebase' ? 'info' : 'default'}
                variant={selectedNoteTypeFilter === 'knowledgebase' ? 'filled' : 'outlined'}
                sx={{ 
                  fontWeight: selectedNoteTypeFilter === 'knowledgebase' ? 600 : 500,
                  fontSize: '0.875rem',
                  px: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  },
                }}
              />
            </Box>
          </Card>

          {/* Sessions Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={60} sx={{ color: 'success.main' }} />
            </Box>
          ) : myNotesData.length === 0 ? (
            <Card 
              sx={{ 
                p: 8, 
                textAlign: 'center',
                border: '2px dashed #e2e8f0',
                bgcolor: '#fafafa',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#64748b', mb: 2 }}>
                No sessions found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {selectedNoteTypeFilter 
                  ? `No sessions with ${selectedNoteTypeFilter} type notes found.`
                  : 'Start adding notes to your chat sessions to see them here.'}
              </Typography>
            </Card>
          ) : (
            <>
              <TableContainer 
                component={Paper}
                sx={{ 
                  mb: 3,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                  borderRadius: 3,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#475569' }}>
                        Session
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#475569' }}>
                        Note Types
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#475569' }}>
                        Notes Count
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#475569' }}>
                        Last Updated
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#475569' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {myNotesData.map((session) => (
                      <TableRow 
                        key={session.session_id} 
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(5, 150, 105, 0.02)',
                          },
                          transition: 'all 0.2s',
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: 600, 
                                mb: 0.5,
                                color: '#1e293b',
                              }}
                            >
                              {session.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {session.message_count} messages
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {session.note_types.map((type: string) => (
                              <Chip
                                key={type}
                                label={type}
                                size="small"
                                color={
                                  type === 'problem' ? 'error' :
                                  type === 'suggestion' ? 'success' :
                                  type === 'knowledgebase' ? 'info' : 'default'
                                }
                                sx={{ 
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={session.note_count}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(5, 150, 105, 0.1)',
                              color: 'success.dark',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(session.updated_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewSession(session.session_id)}
                            sx={{
                              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                              textTransform: 'none',
                              fontWeight: 600,
                              boxShadow: 'none',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #047857 0%, #065f46 100%)',
                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                                transform: 'translateY(-1px)',
                              },
                              transition: 'all 0.2s',
                            }}
                          >
                            View Session
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {Math.ceil(pagination.count / pagination.pageSize) > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                  <Pagination
                    count={Math.ceil(pagination.count / pagination.pageSize)}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontWeight: 500,
                        fontSize: '0.875rem',
                      },
                      '& .Mui-selected': {
                        bgcolor: 'success.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'success.dark',
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MyNotesInterface;

